import { useState, useEffect } from "react";
import { Crown, Zap, CreditCard, ChevronDown, Sparkles } from "lucide-react";
import { getUserSubscription } from "../apis/subscription";
import SubscriptionModal from "./SubscriptionModal";
import { useNavigate } from "react-router-dom";

const SubscriptionStatus = () => {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadSubscription();
  }, []);

  const loadSubscription = async () => {
    try {
      setLoading(true);
      const response = await getUserSubscription();
      if (response.data.success && response.data.data) {
        const sub = response.data.data;
        const expiresAt = new Date(sub.expires_at);
        const isExpired = expiresAt < new Date();
        
        if (!isExpired) {
          setSubscription(sub);
        } else {
          setSubscription(null);
        }
      } else {
        setSubscription(null);
      }
    } catch (error) {
      console.error("Error loading subscription:", error);
      setSubscription(null);
    } finally {
      setLoading(false);
    }
  };

  const handleClick = () => {
    setShowModal(true);
  };

  const handleSubscriptionSuccess = (newSubscription) => {
    setSubscription(newSubscription);
    setShowModal(false);
    // Reload page to reflect changes
    window.location.reload();
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-white/10 border border-white/20">
        <div className="animate-spin rounded-full h-3 w-3 border-2 border-white/30 border-t-white"></div>
      </div>
    );
  }

  if (!subscription) {
    return (
      <>
        <button
          onClick={handleClick}
          className="flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 text-xs font-semibold text-gray-900 rounded-xl transition-all shadow-md hover:shadow-lg border border-gray-300"
          style={{ backgroundColor: "#eefa94" }}
        >
          <CreditCard size={14} className="md:w-4 md:h-4" />
          <span className="hidden sm:inline">Activate Plan</span>
        </button>
        <SubscriptionModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          onSuccess={handleSubscriptionSuccess}
        />
      </>
    );
  }

  const getPlanIcon = (planName) => {
    switch (planName) {
      case 'premium':
        return <Crown size={14} className="text-purple-400" />;
      case 'advanced':
        return <Zap size={14} className="text-blue-400" />;
      default:
        return <CreditCard size={14} className="text-gray-400" />;
    }
  };

  const getPlanColor = (planName) => {
    switch (planName) {
      case 'premium':
        return 'border-purple-400/30 bg-purple-400/10';
      case 'advanced':
        return 'border-blue-400/30 bg-blue-400/10';
      default:
        return 'border-white/20 bg-white/5';
    }
  };

  const expiresAt = new Date(subscription.expires_at);
  const daysUntilExpiry = Math.ceil((expiresAt - new Date()) / (1000 * 60 * 60 * 24));
  const isExpiringSoon = daysUntilExpiry <= 7;

  // Get credit info if available
  const creditInfo = subscription.creditInfo;
  const showCredits = creditInfo && creditInfo.totalCredits > 0;

  return (
    <>
      <button
        onClick={handleClick}
        className={`flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 text-xs font-semibold rounded-xl transition-all border ${getPlanColor(subscription.plan_name)} hover:opacity-80`}
      >
        {getPlanIcon(subscription.plan_name)}
        <span className="hidden sm:inline text-white">
          {subscription.plan_display_name}
        </span>
        {showCredits && (
          <span className="hidden md:inline-flex items-center gap-1 text-white/90 ml-1">
            <Sparkles size={12} />
            <span>{creditInfo.totalAvailableReplies} replies</span>
          </span>
        )}
        <ChevronDown size={12} className="text-white/70" />
      </button>
      <SubscriptionModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSuccess={handleSubscriptionSuccess}
      />
    </>
  );
};

export default SubscriptionStatus;

